import { LitElement, html, isServer, type TemplateResult } from 'lit';
import { customElement } from 'lit/decorators/custom-element.js';
import { property } from 'lit/decorators/property.js';
import { query } from 'lit/decorators/query.js';
import { queryAll } from 'lit/decorators/query-all.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { RovingTabindexController } from '@patternfly/pfe-core/controllers/roving-tabindex-controller.js';
import { FloatingDOMController } from '@patternfly/pfe-core/controllers/floating-dom-controller.js';

import '@rhds/elements/rh-button/rh-button.js';
import '@rhds/elements/rh-icon/rh-icon.js';
import '@rhds/elements/rh-menu/rh-menu.js';

import { RhMenuItem } from '../rh-menu/rh-menu-item.js';

import styles from './rh-menu-dropdown.css' with { type: 'css' };

/** Fired when a user selects an action or link from the menu */
export class MenuDropdownSelectEvent extends Event {
  constructor(
    public selectedItem: RhMenuItem,
    public text: string
  ) {
    super('select', { bubbles: true, composed: true });
  }
}


/**
 * A menu dropdown presents a list of actions or links in a vertically stacked menu,
 * appearing when a user interacts with a toggle button.
 *
 * @summary A collapsible menu for presenting a list of options or actions
 *
 * @alias menu-dropdown
 */
@customElement('rh-menu-dropdown')
export class RhMenuDropdown extends LitElement {
  static readonly styles: CSSStyleSheet[] = [styles];
  private static instances = new Set<RhMenuDropdown>();

  /**
   * whether the dropdown is currently open.
   */
  @property({ type: Boolean, reflect: true }) open = false;

  /**
   * Defines the visual style of the dropdown.
   * Setting it to 'borderless' removes the default border styling.
   */
  @property({ reflect: true }) variant?: 'borderless';

  /**
   * The 'compact' layout reduces spacing and add the rh-icon `ellipsis-vertical-fill`.
   */
  @property({ attribute: 'layout', reflect: true }) layout?: 'compact';

  /**
   * Disables user interaction with the dropdown. When true, the dropdown cannot
   * be opened or interacted with, and appears visually disabled.
   */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /**
   * Provides an accessible name for the dropdown's trigger, improving screen reader support.
   * This label is announced to assistive technologies to describe the purpose of
   * the compact menu dropdown.
  */
  @property({ attribute: 'accessible-label', reflect: true }) accessibleLabel = 'Toggle menu';

  @query('#menu-toggle') menuToggleButton!: HTMLElement;
  @query('#menu-list') menuList!: HTMLElement;
  @queryAll('slot') slotElement!: NodeListOf<HTMLSlotElement>;

  static override readonly shadowRootOptions: ShadowRootInit = {
    ...LitElement.shadowRootOptions,
    delegatesFocus: true,
  };

  static {
    if (!isServer) {
      document.addEventListener('click', function(event) {
        for (const instance of RhMenuDropdown.instances) {
          instance.#outsideClick(event);
        }
      });
    }
  }

  connectedCallback() {
    super.connectedCallback();
    RhMenuDropdown.instances.add(this);
  }

  disconnectedCallback() {
    RhMenuDropdown.instances.delete(this);
  }

  firstUpdated() {
    this.updateComplete.then(() => {
      this.#validateSlotContent();
    });
  }

  get #items() {
    return this.items;
  }

  #outsideClick(event: MouseEvent) {
    const path = event.composedPath();
    if (!path.includes(this)) {
      if (this.open) {
        this.open = false;
      }
    }
  }

  #validateSlotContent() {
    this.slotElement?.forEach((slot: HTMLSlotElement) => {
      const assignedElements = slot.assignedElements({ flatten: true });
      assignedElements.forEach(el => {
        if ( el instanceof HTMLHRElement) {
          el.inert = true;
        }
      });
    });
  }

  #tabindex = RovingTabindexController.of(this, {
    getItems: () => this.#items,
  });

  #float = new FloatingDOMController(this, {
    content: () => this.menuList,
    invoker: () => this.menuToggleButton,
  });

  /**
  * Moves focus to the currently active (focused) item.
  */
  focus() {
    this.#tabindex.items[this.#tabindex.atFocusedItemIndex]?.focus();
  }

  render(): TemplateResult<1> {
    const { alignment, anchor, styles, open } = this.#float;

    return html`
      <div @focusout=${this.#onFocusOut} id="menu-dropdown-container">
        <button id="menu-toggle"
                type="button"
                aria-haspopup="menu"
                aria-expanded="${this.open}"
                @click="${this.#toggleMenu}"
                aria-controls="menu-list"
                aria-disabled="${this.disabled}"
                @keydown="${this.#onToggleKeydown}"
                class="${classMap({
                  boxed: this.variant !== 'borderless',
                  compact: this.layout === 'compact',
                  disabled: this.disabled,
                  open: this.open,
                })}">
            ${this.layout === 'compact' ?
              html`<rh-icon set="ui" accessible-label=${this.accessibleLabel} icon="ellipsis-vertical-fill"></rh-icon>`
              : html` 
              <span class="info-section"> 
                <!-- Use this slot for the toggle label. Keep toggle labels short and succinct. -->
                <slot name="toggle-label"></slot>
              </span>
              <span class="action-icon">
                <rh-icon set="microns" icon="${this.open ? 'caret-up' : 'caret-down'}"></rh-icon>
              </span>
              `
            }
        </button>
        <div id="menu-list"
             ?hidden=${!this.open}
             @click=${this.#onSelect}
             style="${styleMap(styles)}"
             class="${classMap({ [anchor]: !!anchor, [alignment]: !!alignment, open })}"
             @keydown=${this.#onKeyDown}>
          <rh-menu role="menu" aria-labelledby="menu-toggle">
            <!-- 
              Use this slot to provide the menu content. Use the "rh-menu" component 
              for the menu panel, and use "rh-menu-items" to define the individual menu items.
              To organize menu items into groups, use the "rh-menu-item-group" component.
            -->
            <slot></slot>
          </rh-menu>
        </div>
      </div>
    
    `;
  }

  #toggleMenu() {
    if (!this.disabled) {
      this.open = !this.open;

      if (this.open) {
        this.updateComplete.then(async () => {
          await this.#positionMenu();
          this.#focusFirstItem();
        });
      } else {
        this.#float.hide();
      }
    }
  }

  get items(): HTMLElement[] {
    if (!isServer) {
      return Array.from(this.querySelectorAll('rh-menu-item')) as HTMLElement[];
    } else {
      return [];
    }
  }

  #focusFirstItem() {
    this.items[0]?.focus();
  }

  #onToggleKeydown(e: KeyboardEvent) {
    if (!this.disabled) {
      if (['Enter', ' ', 'ArrowDown'].includes(e.key)) {
        e.preventDefault();
        this.open = true;
        this.updateComplete.then(async () => {
          await this.#positionMenu();
          this.#focusFirstItem();
        });
      }
    }
  }

  #handleSelection(target: RhMenuItem) {
    this.open = false;
    this.menuToggleButton.focus();
    this.dispatchEvent(new MenuDropdownSelectEvent(
      target,
      target.textContent ? target.textContent : ''
    ));
    if (target.href) {
      if (target.external) {
        window.open(target.href, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = target.href;
      }
    }
  }

  #onSelect(event: KeyboardEvent & { target: RhMenuItem }) {
    if (event.target instanceof RhMenuItem) {
      this.#handleSelection(event.target);
    }
  }

  #onKeyDown(event: KeyboardEvent & { target: RhMenuItem }) {
    if (event.key === 'Escape') {
      if (this.open) {
        this.open = false;
        this.menuToggleButton.focus();
      }
    } else if (
      event.target instanceof RhMenuItem
      && (event.key === 'Enter' || event.key === ' ')
      && !event.target.disabled
    ) {
      event.preventDefault();
      this.#handleSelection(event.target);
    }
  }

  #onFocusOut(event: FocusEvent) {
    const relatedTarget = event.relatedTarget as Node | null;
    // If the next focused element is outside this component, close the dropdown
    if (
      relatedTarget !== this.menuToggleButton
      && relatedTarget !== this.menuList
      && relatedTarget && !this.contains(relatedTarget)
    ) {
      if (this.open) {
        this.open = false;
      }
    }

    // Also close if nothing is focused (focus left the document)
    if (!relatedTarget) {
      this.open = false;
    }
  }

  async #positionMenu() {
    await this.updateComplete;
    const placement = 'bottom-start';
    const mainAxis = 4;
    const offset = { mainAxis: mainAxis, alignmentAxis: 0 };
    await this.#float.show({ offset: offset, placement: placement });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rh-menu-dropdown': RhMenuDropdown;
  }
}
